import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import apiClient from '@/lib/api-client'
import { API_ENDPOINTS } from '@/lib/constants'

export interface User {
  id: string
  email: string
  name?: string
  roles: string[]
  permissions: string[]
  orgId?: string
  outlet_ids?: string[]
  auth_provider: string
  is_active: boolean
  last_login?: string
  token_balance?: number
  created_at: string
  updated_at: string
}

interface AuthState {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
}

interface AuthActions {
  login: (email: string, password: string) => Promise<void>
  signup: (email: string, password: string, name?: string) => Promise<void>
  logout: () => void
  refreshToken: () => Promise<void>
  initializeAuth: () => void
  clearError: () => void
}

type AuthStore = AuthState & AuthActions

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      // State
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      // Actions
      login: async (email: string, password: string) => {
        set({ isLoading: true, error: null })

        try {
          const response = await apiClient.post(API_ENDPOINTS.AUTH.LOGIN, {
            email,
            password,
          })

          // Extract from nested data structure: response.data.data
          const { access_token, refresh_token } = response.data.data

          // Store tokens
          localStorage.setItem('access_token', access_token)
          localStorage.setItem('refresh_token', refresh_token)

          // Get user info
          const userResponse = await apiClient.get(API_ENDPOINTS.AUTH.ME)
          const user = userResponse.data.data  // Extract from nested structure

          set({
            user,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          })
        } catch (error: any) {
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: error.response?.data?.message || 'Login failed',
          })
          throw error
        }
      },

      signup: async (email: string, password: string, name?: string) => {
        set({ isLoading: true, error: null })
        
        try {
          await apiClient.post(API_ENDPOINTS.AUTH.SIGNUP, {
            email,
            password,
            name,
          })

          // Auto-login after signup
          await get().login(email, password)
        } catch (error: any) {
          set({
            isLoading: false,
            error: error.response?.data?.message || 'Signup failed',
          })
          throw error
        }
      },

      logout: () => {
        localStorage.removeItem('access_token')
        localStorage.removeItem('refresh_token')
        set({
          user: null,
          isAuthenticated: false,
          error: null,
        })
      },

      refreshToken: async () => {
        try {
          const refreshToken = localStorage.getItem('refresh_token')
          if (!refreshToken) {
            throw new Error('No refresh token')
          }

          const response = await apiClient.post(API_ENDPOINTS.AUTH.REFRESH, {
            refresh_token: refreshToken,
          })

          // Extract from nested data structure: response.data.data
          const { access_token, refresh_token: new_refresh_token } = response.data.data
          localStorage.setItem('access_token', access_token)

          // Update refresh token (token rotation)
          if (new_refresh_token) {
            localStorage.setItem('refresh_token', new_refresh_token)
          }
        } catch (error) {
          get().logout()
          throw error
        }
      },

      initializeAuth: () => {
        const token = localStorage.getItem('access_token')
        if (token) {
          // Verify token by getting user info
          apiClient.get(API_ENDPOINTS.AUTH.ME)
            .then((response) => {
              set({
                user: response.data.data,  // Extract from nested structure
                isAuthenticated: true,
              })
            })
            .catch(() => {
              get().logout()
            })
        }
      },

      clearError: () => {
        set({ error: null })
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
)
